import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import unlockModalConfig from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import UnlockModal from "pro-features/components/Modals/src/UnlockModal/UnlockModal";
import type React from "react";
import { useMemo, useState } from "react";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import {
    FilterItemChipdown,
    FiltersNewContainer,
    FiltersNewContent,
} from "app/pages/website-analysis/traffic-sources/search/components/filters/WebsiteKeywordsPageFiltersStyledComponents";
import { BrandedNonBrandedFilterForRankingDistribution } from "app/pages/website-analysis/traffic-sources/search/components/filters/BrandedNonBrandedFilter";
import { VolumeFilterForRankingDistribution } from "app/pages/website-analysis/traffic-sources/search/components/filters/VolumeFilter";
import { CpcFilterForRankingDistribution } from "../../../../website-analysis/traffic-sources/search/components/filters/CpcFilter";
import { SerpFilterForRankingDistribution } from "app/pages/website-analysis/traffic-sources/search/components/filters/SerpFilter";
import { RankingDistributionSingleGraph } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionSingleGraph";
import type { IRankingDistributionTableTopContext } from "./useRankingDistributionTableTopContext";
import { RankingDistributionTableTopContextProvider } from "./useRankingDistributionTableTopContext";
import { RankingDistributionCompareMode } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionCompareMode";
import { RANKING_DISTRIBUTION_DICTIONARY } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionTranslator";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { getRankingDistributionTableTopContext } from "./RankingDistributionContext";
import { MultiIncludeExcludeFilterContainer } from "app/components/multi-include-exclude-filter/MultiIncludeExcludeFilterContainer";
import { multiIncludeExcludeType } from "app/components/multi-include-exclude-filter/consts";
import { RankingTierFilterV2 } from "../../../../website-analysis/traffic-sources/search/components/filters/RankingTierFilterV2";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { PositionTrendsFilterV2 } from "../../../../website-analysis/traffic-sources/search/components/filters/PostitionTrendFilterV2";
const OverviewAndFiltersContainer = styled.div`
    padding: 16px 0 0 0;
`;

const TableTopContainer = styled.div`
    background: ${colorsPalettes.bluegrey[100]};
`;
const FiltersContainer = styled(FiltersNewContent)`
    flex-direction: column;
`;

export interface IButtonProps {
    excelLink: string;
    totalCount: number;
}

export const RankingDistributionTableTop: React.FC<any> = (props) => {
    const { filtersStateObject, onFilterChange, dataParamsAdapter, metric, totalCount, initialFilters, requestBody } =
        props;
    const translate = useTranslation();
    const metricTitle = translate(RANKING_DISTRIBUTION_DICTIONARY.TITLE);
    const [unlockModalOpen, setUnlockModalOpen] = useState(false);
    const isCompare = safeJsonParse(filtersStateObject.pageFilter, []).length > 1;
    const buttonsProps: IButtonProps = {
        excelLink: props.excelLink,
        totalCount,
    };
    const contextValue = useMemo<IRankingDistributionTableTopContext>(() => {
        const { isLoadingData, chosenItems, tableData } = props;
        return getRankingDistributionTableTopContext({
            isLoadingData,
            tableFilters: props.filtersStateObject,
            applyChangesToUrl: () => null,
            isCompare,
            chosenItems,
            tableData,
            onFilterChange,
            metric,
        });
    }, [props.isLoadingData, props.filtersStateObject, props.channelsFilter, props.sourcesFilter]);

    const getOverviewTopComponent = () => {
        return isCompare ? (
            <RankingDistributionCompareMode
                metric={filtersStateObject.metric}
                dataParamsAdapter={dataParamsAdapter}
                metricTitle={metricTitle}
                buttonsProps={buttonsProps}
                requestBody={requestBody}
                pageFilter={filtersStateObject.pageFilter}
            />
        ) : (
            <RankingDistributionSingleGraph
                dataParamsAdapter={dataParamsAdapter}
                buttonsProps={buttonsProps}
                metricTitle={metricTitle}
                requestBody={requestBody}
            />
        );
    };

    return (
        <RankingDistributionTableTopContextProvider value={contextValue}>
            <TableTopContainer>
                <OverviewAndFiltersContainer>
                    <FiltersNewContainer data-automation="search-keywords-filters">
                        <FiltersContainer>
                            <div>
                                <FilterItemChipdown>
                                    <MultiIncludeExcludeFilterContainer filterType={multiIncludeExcludeType.KEYWORD} />
                                </FilterItemChipdown>
                                <FilterItemChipdown>
                                    <MultiIncludeExcludeFilterContainer filterType={multiIncludeExcludeType.URL} />
                                </FilterItemChipdown>
                                <FilterItemChipdown>
                                    <PositionTrendsFilterV2 />
                                </FilterItemChipdown>
                                <FilterItemChipdown>
                                    <RankingTierFilterV2 />
                                </FilterItemChipdown>
                                <FilterItemChipdown>
                                    <BrandedNonBrandedFilterForRankingDistribution />
                                </FilterItemChipdown>
                                <FilterItemChipdown>
                                    <VolumeFilterForRankingDistribution />
                                </FilterItemChipdown>
                                <span style={{ display: "inline-table" }}>
                                    <FilterItemChipdown>
                                        <CpcFilterForRankingDistribution />
                                    </FilterItemChipdown>
                                    <FilterItemChipdown>
                                        <SerpFilterForRankingDistribution />
                                    </FilterItemChipdown>
                                </span>
                            </div>
                        </FiltersContainer>
                    </FiltersNewContainer>
                </OverviewAndFiltersContainer>
                <div>
                    {unlockModalOpen && (
                        <UnlockModal
                            isOpen={true}
                            onCloseClick={() => {
                                setUnlockModalOpen(false);
                            }}
                            location={`${LocationService.getCurrentLocationTracking()}/Download Table`}
                            {...unlockModalConfig().DownloadTable}
                        />
                    )}
                </div>
                {getOverviewTopComponent()}
            </TableTopContainer>
        </RankingDistributionTableTopContextProvider>
    );
};
